import React from 'react';
import { app, authentication } from '@microsoft/teams-js';
import { MdErrorOutline, MdRefresh } from 'react-icons/md';
import AegisAiChat from '../Chat/AegisAiChat';
import '../../../Styles/AegisAiChat.css';

// Outside the Teams client app.initialize() never settles, so give up after this long.
const TEAMS_INIT_TIMEOUT_MS = 8000;
const TEAMS_THEMES = new Set(['default', 'dark', 'contrast']);

const withTimeout = (promise, ms) => Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
]);

const toTheme = (theme) => (TEAMS_THEMES.has(theme) ? theme : 'default');

// The Aegis AI tab of the Microsoft Teams app. The user is signed in silently with Teams single sign-on,
// so there is no Aegis login; the page follows the Teams light, dark and high-contrast themes live.
export default function TeamsChat() {
    const [status, setStatus] = React.useState('loading');
    const [error, setError] = React.useState('');
    const [theme, setTheme] = React.useState('default');
    const [userName, setUserName] = React.useState('');
    const [token, setToken] = React.useState('');
    const [attempt, setAttempt] = React.useState(0);

    // Teams caches the sign-on token and renews it before it expires, so asking per request is cheap.
    const getToken = React.useCallback(async () => {
        const fresh = await authentication.getAuthToken();
        setToken(fresh);
        return fresh;
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await withTimeout(app.initialize(), TEAMS_INIT_TIMEOUT_MS);
            } catch {
                if (!cancelled) setStatus('outside-teams');
                return;
            }
            try {
                const context = await app.getContext();
                if (cancelled) return;
                setTheme(toTheme(context.app?.theme));
                setUserName(context.user?.displayName || context.user?.userPrincipalName || '');
                app.registerOnThemeChangeHandler((next) => setTheme(toTheme(next)));
                await getToken();
                if (cancelled) return;
                setStatus('ready');
                app.notifySuccess();
            } catch (err) {
                console.error('Teams sign-in failed:', err);
                if (cancelled) return;
                setError(typeof err === 'string' ? err : err?.message || 'Unknown error');
                setStatus('sign-in-failed');
                app.notifyFailure({ reason: app.FailedReason.AuthFailed, message: String(err?.message || err) });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [attempt, getToken]);

    if (status === 'ready') {
        return <AegisAiChat teams={{ token, getToken, userName, theme }} />;
    }

    return (
        <div className={`hc-ai-page hc-aic-page is-teams is-teams-${theme}`}>
            <div className="hc-aic-teams-state d-flex flex-column align-items-center justify-content-center text-center">
                {status === 'loading' ? (
                    <>
                        <span className="spinner-border hc-aic-teams-spinner" role="status" aria-hidden="true" />
                        <p className="hc-aic-empty-text mt-3 mb-0">Signing you in…</p>
                    </>
                ) : (
                    <>
                        <MdErrorOutline size={40} className="hc-aic-teams-error-icon" />
                        <h3 className="hc-aic-empty-title mt-3">
                            {status === 'outside-teams' ? 'Open Astes AI in Microsoft Teams' : "We couldn't sign you in"}
                        </h3>
                        <p className="hc-aic-empty-text">
                            {status === 'outside-teams'
                                ? 'This page only works inside the Astes AI app in Microsoft Teams.'
                                : `Your organization may still need to approve the Astes AI app. (${error})`}
                        </p>
                        {status === 'sign-in-failed' ? (
                            <button type="button" className="hc-aic-teams-retry d-inline-flex align-items-center gap-2 fw-bold" onClick={() => {
                                setStatus('loading');
                                setAttempt((count) => count + 1);
                            }}>
                                <MdRefresh size={18} />
                                Try again
                            </button>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}
